import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem('lang', code);
      // Mark as explicit user choice — geo-IP and browser detection must respect this
      localStorage.setItem('lang_user_picked', '1');
      localStorage.removeItem('lang_auto_detected');
    } catch (_) { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher-button"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold bg-slate-800/60 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 w-48 bg-slate-900 border border-slate-700 rounded-lg shadow-xl shadow-black/40 py-1 z-50 max-h-[70vh] overflow-y-auto"
          role="listbox"
          data-testid="lang-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === i18n.language;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => changeLang(l.code)}
                data-testid={`lang-${l.code}`}
                role="option"
                aria-selected={active}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors text-left ${
                  active
                    ? 'bg-amber-500/15 text-amber-300'
                    : 'text-slate-200 hover:bg-slate-800'
                }`}
              >
                <span className="text-lg leading-none">{l.flag}</span>
                <span className="flex-1 font-medium">{l.name}</span>
                <span className="text-[10px] text-slate-500 font-bold">{l.label}</span>
                {active && <Check className="w-3.5 h-3.5 text-amber-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
